import { useState } from 'react';

import { Box, Button, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { LeadDetailsVehicle } from '@ekhodealer/ekho-common';

import { AccessoriesListView } from '@common/components/AccessoriesListView';
import { DetailedInfoLineItem } from '@common/components/DetailedInfoLineItem';
import { ChevronDown12, ChevronUp12 } from '@common/components/icons';
import { SpecsListView } from '@common/components/SpecsListView';

interface LeadsVehicleProps {
	vehicle: LeadDetailsVehicle;
	/** Optional price label to display (e.g., "Canceled"). If not provided, shows vehicle.totalPrice */
	priceLabel?: string;
	/** Optional color for the price label */
	priceLabelColor?: 'primary' | 'secondary' | 'error';
	/** Whether the price label should be italic */
	priceLabelItalic?: boolean;
}

/**
 * Reusable vehicle display component for leads sections
 * Shows vehicle image, name, specs/accessories count, price, and expand button
 * Expandable to show full specs and accessories lists
 */
export const LeadsVehicle = ({
	vehicle,
	priceLabel,
	priceLabelColor = 'primary',
	priceLabelItalic = false,
}: LeadsVehicleProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const specsCount = vehicle.specs.length;
	const accessoriesCount = vehicle.partsGarmentsAndAccessories.length;
	const hasExpandableContent = specsCount > 0 || accessoriesCount > 0;
	const displayPrice = priceLabel || vehicle.totalPrice;

	return (
		<Box rounding='12' padding='12' background='primary'>
			<Stack itemsSpacing='12'>
				<DetailedInfoLineItem
					imageUrl={vehicle.imgUrl}
					showPlaceholderImage
					primaryLabel={vehicle.name}
					secondaryLabel={`Specs (${specsCount}) • Accessories (${accessoriesCount})`}
					rightAccessory={
						<Group itemsSpacing='12' itemsAlignY='center' itemsAlignX='center' noWrap>
							<Text
								size='bodySmall'
								weight={priceLabel ? 'regular' : 'semibold'}
								color={priceLabelColor}
								isItalic={priceLabelItalic}>
								{displayPrice}
							</Text>
							{hasExpandableContent && (
								<Button
									variant='secondary'
									size='small'
									icon={isExpanded ? <ChevronUp12 /> : <ChevronDown12 />}
									iconSide='right'
									onPress={() => setIsExpanded(!isExpanded)}></Button>
							)}
						</Group>
					}
				/>

				{isExpanded && (
					<Stack itemsSpacing='16'>
						<SpecsListView specs={vehicle.specs} />
						<AccessoriesListView accessories={vehicle.partsGarmentsAndAccessories} />
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
